import {
  IBaseDataLayer,
  IDataLayerVariable,
  PageType,
} from "@almamedia/amr-adtech-stack/lib/services/dataLayer";

interface ICustomVariables extends IBaseDataLayer {
  viewPort: IDataLayerVariable<number>;
  destinationURL: IDataLayerVariable<string>;
  subCategory: IDataLayerVariable<string>;
  articleType: IDataLayerVariable<string>;
  validate: IDataLayerVariable<boolean>;
}

type DataLayerImplementation = {
  name: string;
  id: string;
  data: ICustomVariables;
};

const dataLayer: DataLayerImplementation = {
  name: "Talouselama.fi data layer",
  id: "talouselamafi-dl",
  data: {
    viewPort: {
      name: "viewport",
      get: (): number => {
        return window.digitalData?.page?.attributes?.width ?? 0;
      },
    },
    sysEnv: {
      name: "system environment", // friendly name
      get: (): string => window?.digitalData?.page.pageInfo?.sysEnv ?? "",
    },
    destinationURL: {
      name: "destination url",
      get: (): string => {
        return window.digitalData?.page?.pageInfo?.locationHref ?? "";
      },
    },
    primaryCategory: {
      name: "primary category", // friendly name
      get: (): string =>
        window?.digitalData?.page?.category?.primaryCategory ?? "etusivu",
    },
    subCategory: {
      name: "sub category",
      get: (): string => window?.digitalData?.page?.category?.subCategory ?? "",
    },
    articleType: {
      name: "article type",
      get: (): string => window?.digitalData?.page?.attributes?.content?.articleType ?? "",
    },
    pageId: {
      // unique name
      name: "page ID", // friendly name
      get(): string {
        return window?.digitalData?.page?.pageInfo?.pageID ?? "";
      },
    },
    locationHref: {
      name: "location Href url", // friendly name
      get: (): string =>
        window?.digitalData?.page?.pageInfo?.locationHref ??
        window.location.href,
    },
    section: {
      name: "section for ad load",
      get: (): string => {
        const device = window.innerWidth < 768 ? "mobile" : "desktop";
        const pageType = window?.digitalData?.page?.category?.pageType ?? null;

        function wide() {
          if (window.innerWidth >= 1250) {
            return "_wide";
          }
          return "";
        }

        if (pageType === "article") {
          return `${device}_article${wide()}`;
        }

        if (
          pageType === "frontpage" ||
          pageType === "sectionfrontpage" ||
          pageType === "searchpage" ||
          pageType === "searchresultpage" ||
          pageType === "itempage"
        ) {
          return `${device}_section${wide()}`;
        }

        const section = "fallback_section";

        return section;
      },
    },
    ads: {
      name: "enable / disable ads",
      get: (): boolean => {
        return true;
      },
    },
    pageTags: {
      name: "page tags", // friendly name
      get: (): string[] => {
        let tags: string[] = [];
        if (window?.digitalData?.page?.category?.tags) {
          const ddlTags = window.digitalData.page.category.tags as string[];
          tags = ddlTags.map((tag) => tag.toLowerCase());
        }
        return tags;
      },
    },
    validate: {
      // unique name
      name: "validate", // friendly name
      get: (): boolean => {
        return (
          window?.digitalData?.page?.category?.primaryCategory ===
          "artikkelisivu"
        );
      },
    },
    pageType: {
      name: "Page type",
      get: (): PageType => {
        const pageType = window?.digitalData?.page?.category?.pageType;
        if (pageType === "artikkelisivu") {
          return PageType.Article;
        }
        return window?.digitalData?.page?.category?.pageType ?? "";
      },
    },
    subCategory1: {
      name: "sub category 1", // friendly name
      get: (): string =>
        window?.digitalData?.page?.category?.subCategory1 ?? "",
    },
    accountType: {
      name: "user account type",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.accountType;
      },
    },
    alma_id: {
      name: "alma_id hash",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.almaIDHash;
      },
    },
    amid: {
      name: "alma master id",
      get: (): string | undefined => {
        try {
          return window.localStorage.getItem("gravitoOPUid") ?? undefined;
        } catch (e) {
          return undefined;
        }
      },
    },
    cdime: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdime ?? window?.ALMA?.tunnus?.getUser()?.cdime;
      },
    },
    cdimu: {
      name: "sha256 hash of email",
      get: (): string | undefined => {
        const ddlProfile =
          window?.digitalData?.user?.[0]?.profile?.[0]?.profileInfo;
        return ddlProfile?.cdimu;
      },
    },
    issueDate: {
      name: "article issue date",
      get: (): string => {
        return window?.digitalData?.page?.pageInfo?.issueDate ?? "";
      },
    },
    cid: {
      name: "content id",
      get: (): string => {
        return window?.digitalData?.page?.attributes?.content?.cid ?? "";
      },
    },
  },
};

export default dataLayer;
export type { ICustomVariables };
